import type { JSX } from "react";
import { useEffect, useState } from "react";
import type {
  ReplayBlock as ReplayBlockModel,
  ReplayNarration,
  ReplayPayload,
} from "@shared/types";
import type { BlockRendererProps } from "./registry";
import { ReplayView } from "../loom-replay/replay-view";

/**
 * Replay-block renderer (FEATURE_IA_REPLAY): a lightweight `replay` block carries
 * only a loom session id + title; on mount this fetches the heavy `ReplayPayload`
 * from the host's public serve route `GET /api/replay/<sessionId>` (curated demo
 * sessions only), shows a loading / error state, then renders the converted
 * session through `ReplayView` (the ported loomweb block-views). LOOM-PRODUCT-
 * COUPLED; only ever rendered when the backend host-injected the block, which is
 * itself gated by `config.replayEnabled`.
 */
export function ReplayBlockView({ block }: BlockRendererProps): JSX.Element {
  const replay = block as ReplayBlockModel;
  const [payload, setPayload] = useState<ReplayPayload | null>(null);
  const [narration, setNarration] = useState<ReplayNarration | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPayload(null);
    setNarration(null);
    setError(null);
    const sid = encodeURIComponent(replay.sessionId);
    void (async (): Promise<void> => {
      try {
        const res = await fetch(`/api/replay/${sid}`);
        if (!res.ok) {
          throw new Error(`replay fetch failed: ${res.status}`);
        }
        const data = (await res.json()) as ReplayPayload;
        // Defensive: a malformed / empty body (no messages array) would crash the
        // renderer (.map on undefined) and take down the whole answer. Treat it as
        // an error so the card shows a graceful failure state instead.
        if (!Array.isArray(data.messages)) {
          throw new Error("replay payload missing messages[]");
        }
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    // Narration (旁白配音) is optional: a 404 means this session has none, which
    // is not an error — the replay just falls back to silent per-block reveal.
    void (async (): Promise<void> => {
      try {
        const res = await fetch(`/api/replay/${sid}/narration`);
        if (!res.ok) return;
        const data = (await res.json()) as ReplayNarration;
        if (!cancelled && Array.isArray(data.chapters) && data.chapters.length > 0) {
          setNarration(data);
        }
      } catch {
        // Ignore: narration is best-effort enrichment.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [replay.sessionId]);

  return (
    <div className="replay-block-card">
      <div className="replay-card-head">
        <span className="replay-card-kicker">会话回放</span>
        <span className="replay-card-title">{replay.title}</span>
      </div>
      {error !== null ? (
        <div className="replay-card-state replay-card-error">回放加载失败，请稍后再试。</div>
      ) : payload === null ? (
        <div className="replay-card-state">正在加载会话回放…</div>
      ) : (
        <ReplayView
          payload={payload}
          sessionId={replay.sessionId}
          {...(narration !== null ? { narration } : {})}
        />
      )}
    </div>
  );
}
